import type { StorageLike } from 'pinia-plugin-persistedstate';

import { cookieSharedDomainConf, removeAccessTokenFromCookies, saveAccessTokenToCookies } from '@/helpers/user-helper';
import { captureMessageForSentry } from '@/libraries/sentry';
import Cookies from 'js-cookie';

/** Pinia Persisted State Plugin namespace à la jw/user. */
export const STORE_NAMESPACE = 'jw';

export const cookieStorage: StorageLike = {
	getItem: (key: string) => {
		if (import.meta.server) return null;
		return Cookies.get(`${STORE_NAMESPACE}_${key}`) || Cookies.get(`${STORE_NAMESPACE}/${key}`) || null;
	},

	setItem: (key: string, value: string) => {
		if (import.meta.server) return;

		const durationDay = 60 * 60 * 24;
		const duration = durationDay * 30 * 12 * 2; // two years
		const cookieExpiryDate = new Date(Date.now() + duration * 1000);

		// holds only the necessary properties that are needed for the initial server render
		const storeValue = JSON.parse(value);
		const { accessToken, ...userStoreValue } = storeValue;

		if (accessToken) {
			saveAccessTokenToCookies(accessToken, cookieExpiryDate);
		} else {
			removeAccessTokenFromCookies();
		}

		const stringifiedUserStoreValue = JSON.stringify(userStoreValue);

		// keep it temporarily
		Cookies.set(`${STORE_NAMESPACE}/${key}`, stringifiedUserStoreValue, {
			expires: cookieExpiryDate,
			path: '/',
		});

		const cookieName = `${STORE_NAMESPACE}_${key}`;
		Cookies.set(cookieName, stringifiedUserStoreValue, {
			expires: cookieExpiryDate,
			secure: true,
			sameSite: 'lax',
			/*
				Important : IF the path and domain are ever needed to be removed or changed
				we need to make sure that the old cookies are removed.
				Otherwise the browser will send duplicate cookies to the server.
			*/
			...cookieSharedDomainConf,
		});

		// Check if jwUser cookie exceeds 4KB limit
		cookieJwUserSizeExceedsLimitCheck(cookieName, stringifiedUserStoreValue);

		/*
			Because of adding domain and path to the jw_user cookie
			it is possible that old cookies without domain and path are still present.
			We need to remove them to avoid sending duplicate cookies to the server.
		*/
		if (detectDuplicateCookies(cookieName)) {
			Cookies.remove(cookieName);
		}
	},
};

let isCookieSizeErrorLogged = false;
function cookieJwUserSizeExceedsLimitCheck(name: string, value: string) {
	if (isCookieSizeErrorLogged) return;
	const maxCookieSize = 3584; // 3.5KB
	const cookieOverhead = `${name}=`.length; // account for the name and '='
	const cookieSize = cookieOverhead + new Blob([value]).size;
	if (cookieSize > maxCookieSize) {
		captureMessageForSentry(
			'[cookieStorage.ts] jw_user cookie exceeds 3.5KB limit',
			{
				cookieSize,
			},
			'error'
		);
		isCookieSizeErrorLogged = true;
	}
}

// Detect duplicates by name using document.cookie
function detectDuplicateCookies(name: string) {
	if (import.meta.server) return false;

	const raw = document.cookie || '';
	const values = raw
		.split(/;\s*/) // split "a=1; b=2; a=3" → ["a=1","b=2","a=3"]
		.filter(s => s.startsWith(name + '='))
		.map(s => decodeURIComponent(s.slice(name.length + 1)));

	return values.length > 1;
}
